import { useEffect } from "react";
import { X } from "lucide-react";
import { DocumentRenderer } from "@zirro/renderer";
import type { Page } from "@zirro/schema";
import { useEditorStore, type PageLayoutOption } from "../store";

const PREVIEW_WIDTH = 1280;
const CARD_WIDTH = 300;
const CARD_HEIGHT = 210;
const SCALE = CARD_WIDTH / PREVIEW_WIDTH;

/** A scaled-down render of the page with a layout's nodes swapped in. */
function LayoutThumbnail({ page, option }: { page: Page; option: PageLayoutOption }) {
  const document = useEditorStore((s) => s.document);
  return (
    <div className="zirro-layout-thumb" style={{ width: CARD_WIDTH, height: CARD_HEIGHT }} aria-hidden="true">
      <div style={{ width: PREVIEW_WIDTH, height: CARD_HEIGHT / SCALE, transform: `scale(${SCALE})` }}>
        <DocumentRenderer
          document={document}
          mode="preview"
          activePage={{ id: page.id, slug: page.slug, title: page.title, nodes: option.nodes }}
        />
      </div>
    </div>
  );
}

/** Lets the owner swap a page's design for one of the template's layouts. */
export function LayoutPicker({ page, onClose }: { page: Page; onClose: () => void }) {
  const layouts = useEditorStore((s) => s.pageLayouts).filter((l) => l.pageSlug === page.slug);
  const replacePageNodes = useEditorStore((s) => s.replacePageNodes);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="zirro-layout-backdrop" role="presentation" onClick={onClose}>
      <div
        className="zirro-layout-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={`Layouts for ${page.title}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="zirro-layout-head">
          <div>
            <h3>Choose a layout for {page.title}</h3>
            <p>Replaces this page's sections. You can undo it.</p>
          </div>
          <button type="button" className="zirro-ed-btn" aria-label="Close layouts" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        <div className="zirro-layout-grid">
          {layouts.map((option) => (
            <button
              key={option.id}
              type="button"
              className="zirro-layout-card"
              onClick={() => {
                replacePageNodes(page.id, option.nodes);
                onClose();
              }}
            >
              <LayoutThumbnail page={page} option={option} />
              <span className="zirro-layout-card__name">{option.name}</span>
              {option.description ? <span className="zirro-layout-card__desc">{option.description}</span> : null}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
